/**
 * @module Router
 *
 */
import React, { useEffect, useMemo, useContext } from "react";
import { AnalyticsService } from "app/services";
import {
  Route,
  Redirect,
  Switch,
  withRouter,
  useHistory,
} from "react-router-dom";

/** Routes */
import Benefits from "app/routes/Benefits";
import SignIn from "app/routes/SignIn";
import SignUp from "app/routes/SignUp";
import ForgotPassword from "app/routes/ForgotPassword";
import ResetPassword from "app/routes/ResetPassword";
import Upgrade from "app/routes/Upgrade";
import Trial from "app/routes/Trial";
import PublicProgram from "app/routes/PublicProgram";
import {
  UnsubscribeInvitation,
  UnsubscribeSpace,
  UnsubscribeUser,
} from "app/routes/Unsubscribe";

import PrivateRoute from "./components/Wrappers/PrivateRoute";
import { routerData } from "./RouterData";
import {
  LOCAL_STORAGE_AUTH_TOKEN_NAME,
  LOCAL_STORAGE_AUTH_TOKEN_NAME_TEST,
} from "./components/Sidebar/indexModel";
import useStoreActions from "./utils/hooks/useStoreActions";
import { fetchSelf } from "./actions/user";
import i18n from "app/i18n";
import { getCookie, setCookie } from "./utils/cookie";
import NewSession from "./routes/PublicCalendar/NewSession";
import Confirmation from "./routes/PublicCalendar/Confirmation";
import { UserContext } from "app/contexts/UserContext";
import NewFacilitySession from "./routes/PublicCalendar/NewFacilitySession";
import PublicFacilityPrograms from "./routes/PublicFacilityPrograms";
import StartYourFreeTrial from "./routes/StartYourFreeTrial";
import StartPaywallForCoaches from "./routes/StartPaywallForCoaches";
import { hasCoachPro } from "./utils/subscription";
import Purchase from "./routes/Purchase";

/**
 * @function AppRouter
 *
 */
const LOCAL_STORAGE_COACH_INVITE_TOKEN = "coachPaywallUrl";
const AppRouter = () => {
  const history = useHistory();

  const { user } = useStoreActions({ user: fetchSelf });
  const pending = user.pending || user.data == null;

  useEffect(() => {
    AnalyticsService.page();

    const coachPaywallUrl = localStorage.getItem(
      LOCAL_STORAGE_COACH_INVITE_TOKEN,
    );
    if (coachPaywallUrl && coachPaywallUrl === "true") {
      if (hasCoachPro(user)) {
        return localStorage.removeItem(LOCAL_STORAGE_COACH_INVITE_TOKEN);
      }
      return history.push("start-coach-pro?plan=pro");
    }
  }, [history]);

  const token =
    typeof window !== "undefined" && localStorage
      ? (localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_NAME) ??
        localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_NAME_TEST))
      : null;

  useMemo(() => {
    if (token && !pending && user && user.data?.locale) {
      i18n.changeLanguage(user.data?.locale);
      setCookie("locale", user.data?.locale, 365);
    } else if (token) {
      const locale = getCookie("locale");

      if (i18n.language !== locale) {
        i18n.changeLanguage(locale);
      }
    } else {
      i18n.changeLanguage(
        (navigator.language || navigator.userLanguage).split("-")[0],
      );
    }
  }, [pending, user, token]);

  const userRole = useContext(UserContext);

  return (
    <Switch>
      {/* Public routes */}
      <Route path="/benefits" component={Benefits} />
      <Route path="/reset" component={ForgotPassword} />
      <Route path="/upgrade" component={Upgrade} />
      <Route path="/trial" component={Trial} />
      <Route path="/password/edit" component={ResetPassword} />
      <Route path="/login" component={() => <Redirect to="/signin" />} />
      <Route path="/signin" component={SignIn} />
      <Route
        path={[
          "/signup/:user_role?",
          "/i/:team_token/:team_role?",
          "/invitation/:invitation_token",
          "/l/:invitation_token",
        ]}
        component={SignUp}
      />
      <Route
        path="/public/unsubscribe/invitation/:invitationId"
        component={UnsubscribeInvitation}
      />
      <Route
        path="/public/unsubscribe/user/:token"
        component={UnsubscribeUser}
      />
      <Route
        path="/public/unsubscribe/:spaceId/:token"
        component={UnsubscribeSpace}
      />
      <Route
        path="/public/programs/:program_id/view"
        component={PublicProgram}
      />
      <Route path="/public/availability" component={NewSession} />
      <Route
        path="/public/facility_availability"
        component={NewFacilitySession}
      />
      <Route path="/public/programs" component={PublicFacilityPrograms} />
      <Route path="/public/confirmation" component={Confirmation} />
      <Route path="/start-trial" component={StartYourFreeTrial} />
      <Route path="/purchase/:plan/:sport" component={StartPaywallForCoaches} />
      <Route
        path={["/purchase/success", "/purchase/cancel"]}
        component={Purchase}
      />

      {/* Private routes */}
      {routerData.map((route, index) => (
        <Route key={index} path={route.path} exact={route.exact}>
          {route.redirectTo && user.self ? (
            <Redirect from={route.path} to={route.redirectTo(userRole)} />
          ) : (
            <PrivateRoute
              permission={route?.permission || ""}
              permissionObject={route?.permissionObject || ""}
            >
              <route.main />
            </PrivateRoute>
          )}
        </Route>
      ))}
    </Switch>
  );
};

export default withRouter(AppRouter);
